<template>
  <div
    class="top-container"
    :class="{
      'top-container--gallery-open': isGalleryOpen && isMqLarger,
      'top-container--gallery-closed': !isGalleryOpen && isMqLarger,
    }"
  >
    <component
      :is="topGalleryComponent"
      :key="isMqLarger"
      :class="{ 'top-gallery--hidden': isGalleryFadedOut }"
      :isSubscription="isSubscription"
      v-bind="props"
      v-on="events"
    ></component>
    <PurchasePod :isSubscription="isSubscription">
      <slot />
    </PurchasePod>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex'
import { isEmpty } from '@/helpers/object'

const BACKGROUND_COLOR = {
  normal: '#f7f7f7',
  subscription: '#ffffff',
}

export default {
  name: 'TopContainer',
  components: {
    // Imported dynamically to improve webpack bundle sizes
    PurchasePod: () =>
      import(
        /*webpackChunkName: "purchase-pod-top-container" */
        '@/components/ProductPage/PurchasePod/PurchasePod.vue'
      ),
  },

  props: {
    isSubscription: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isGalleryFadedOut: false,
      isEmpty,
    }
  },

  computed: {
    ...mapState('product', ['slideIndex', 'slideResetter', 'isGalleryOpen']),

    ...mapGetters({
      currentVariant: 'product/currentVariant',
      isMqLarger: 'viewport/isMqLarger',
      product: 'product/product',
      vpWidth: 'viewport/width',
    }),

    topGalleryComponent() {
      const componentName = this.isMqLarger ? 'TopGallery' : 'TopGalleryMobile'
      return () => import(/* webpackChunkName: "[request]" */ `./TopGallery/${componentName}.vue`)
    },

    backgroundColor() {
      return this.isSubscription ? BACKGROUND_COLOR.subscription : BACKGROUND_COLOR.normal
    },

    props() {
      return this.isMqLarger
        ? {
            backgroundColor: this.backgroundColor,
            isBigGrid: this.isSubscription,
          }
        : {
            backgroundColor: this.backgroundColor,
            currentVariant: this.currentVariant,
            isGalleryOpen: this.isGalleryOpen,
            product: this.product || {},
            slideIndex: this.slideIndex,
            slideResetter: this.slideResetter || [],
            vpHeight: this.vpHeight,
            vpWidth: this.vpWidth,
          }
    },

    events() {
      return this.isMqLarger
        ? {}
        : {
            mounted: () => this.setTopGalleryMounted(true),
            'set-slide-index': this.setSlideIndex,
          }
    },
  },

  watch: {
    vpWidth: 'fadeGalleryOutIn',
  },

  methods: {
    ...mapMutations({
      setSlideIndex: 'product/setSlideIndex',
      setTopGalleryMounted: 'product/setTopGalleryMounted',
    }),

    fadeGalleryOutIn() {
      this.isGalleryFadedOut = true
      this.$nextTick(() => (this.isGalleryFadedOut = false))
    },
  },
}
</script>

<style lang="scss" scoped>
@import './pdp-settings';

$max-width: 1700px;

.top-container {
  background: $color-white;

  @media ($mq-larger) {
    display: flex;
    position: relative;
    z-index: 11;
  }

  @media (min-width: $max-width) {
    margin: 0 auto;
    max-width: $max-width;
  }

  &--gallery-open {
    animation: fadein $transition-time-slow forwards;
    max-width: none;
  }

  &--gallery-closed {
    animation: fadebackin $transition-time-slow forwards;
  }
}

// Duplicated keyframes are needed to restart the animation (https://css-tricks.com/restart-css-animation)
@keyframes fadein {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes fadebackin {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
